import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DebouncedSearchField from "../../components/DebouncedSearchField/DebouncedSearchField";
import PageTitleDescription from "../../components/PageTitleDescription/PageTitleDescription";
import PatientsTable from "../../components/Tables/PatientsTable/PatientsTable";
import { ActiveStatus, BooleanEnum, OrderDirection, PageSize } from "../../enums/Common";
import { IPatientFilters } from "../../interfaces/Patient";
import ButtonCreatePatient from "../../components/Buttons/ButtonCreatePatient/ButtonCreatePatient";
import { IToggleButton } from "../../interfaces/Common";
import { ToggleButtons } from "../../components/ToggleButtons/ToggleButtons";
import StatusDropdown from "../../components/Dropdowns/StatusDropdown/StatusDropdown";

const PatientsDashboard = () => {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<IPatientFilters>({
    _total_count: 1,
    page_size: PageSize.Default,
    page: 1,
    _order_by:
      searchParams.get("_order_by") || `client_id ${OrderDirection.Asc}`,
    name: searchParams.get("name") || undefined,
    isShortlist: Number(searchParams.get("isShortlist")) || undefined,
    id: searchParams.get("id") || undefined,
    isDeleted: ActiveStatus.Active, // Default to showing Active patients
  });

  const toggleButtons = useMemo(
    (): IToggleButton[] => [
      {
        label: "Shortlist",
        onClick: () =>
          setFilters((prev) => ({
            ...prev,
            page: 1,
            isShortlist: BooleanEnum.True,
          })),
      },
      {
        label: "All",
        onClick: () =>
          setFilters((prev) => ({
            ...prev,
            page: 1,
            isShortlist: undefined,
          })),
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col gap-5">
      <div className="flex flex-col gap-4 pr-5 xl:pr-0">
        <div className="flex flex-col gap-4 justify-between sm:flex-row">
          <PageTitleDescription
            className="w-full xl:w-fit"
            title="Patients"
            description="Search and select a patient to view their test results, orders and
          information."
          />

          <ButtonCreatePatient
            className="shrink-0 self-center"
            onCreated={() => setFilters((prev) => ({ ...prev, page: 1 }))}
          />
        </div>

        <div className="flex flex-col gap-4 justify-between lg:flex-row lg:items-center">
          <ToggleButtons
            buttons={toggleButtons}
            initialActiveIndex={filters.isShortlist ? 0 : 1}
          />

          <div className="flex flex-col justify-between items-stretch gap-4 sm:items-start md:flex-row lg:items-center">
            <StatusDropdown
              status={filters.isDeleted}
              onChange={(isDeleted) =>
                setFilters((prev) => ({ ...prev, page: 1, isDeleted }))
              }
            />
            <DebouncedSearchField
              placeholder="Search by Patient Name or Patient ID"
              value={filters.name}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  page: 1,
                  name: value,
                }))
              }
            />
          </div>
        </div>
      </div>

      <div className="grow h-0">
        <PatientsTable filters={filters} setFilters={setFilters} />
      </div>
    </div>
  );
};

export default PatientsDashboard;
